/* eslint-disable no-irregular-whitespace */
// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import {
  Box,
  Checkbox,
  ColorInput,
  Group,
  LoadingOverlay,
  SegmentedControl,
  Select,
  SimpleGrid,
  Slider,
  Space,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import React, { useEffect, useState } from 'react';
import { IconArrowDownBar, IconChevronDown, IconLetterCase } from '@tabler/icons';
import PDFViewer from '../../../Atoms/PDFViewer';
import { DocumentLayout, PdfS } from '../../../Service/PdfS';
import { DataS } from '../../../Service/DataS';
import PanePaper from '../../../Molecules/PanePaper';
import { TestS } from '../../../TestS';
import { RoutingS } from '../../../Service/RoutingS';
import AutocompleteResourceTypeItem from '../../../Organismns/Resources/table/AutocompleteResourceTypeItem';
import { ThemeText, ThemeTitle } from '../../../Atoms/ThemeText';
import { useBankAccount } from '../../../Service/hooks/useBankAccount';
import BookitupTextInput from '../../../Atoms/inputs/BookitupTextInput';

const DocumentLayoutSettings: React.FC<{
  lastModified: Date;
  logoAvailable: boolean;
}> = ({ lastModified, logoAvailable }) => {
  const [loading, setLoading] = useState(false);
  const { bankAccount, editBankAccount } = useBankAccount();
  const [documentLayout, setDocumentLayout] = useState<DocumentLayout>();
  const {
    logoSize,
    hideLogoOnDocuments,
    infoBoxPlacement,
    contactName,
    showFounderPersonalName,
    pageNumberPosition,
    fontColor,
    backgroundColor,
    backgroundTransparent,
    showBorders,
    logoPosition,
    showLineNumber,
    hideBankAccount,
    customSenderInfo,
    headerFontSize,
    defaultFontSize,
    subtextFontSize,
    profileInfoFontSize,
    includeEpcQrCode,
    includePaypalQrCode,
  } = documentLayout ?? {};

  const { paypalUserId } = bankAccount ?? {};

  useEffect(() => {
    RoutingS.changeTitle('Document-Layout');
    setLoading(true);
    PdfS.getDocumentLayout()
      .then((layout) => {
        if (layout) {
          setDocumentLayout(layout);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const patchDocumentLayout = (key: string, value: unknown) => {
    if (documentLayout) {
      PdfS.editDocumentLayout(documentLayout.id, { [key]: value }).then((layout) => {
        if (layout) {
          setDocumentLayout(layout);
        }
      });
    }
  };

  const selectedFontValue = documentLayout ? DataS.fontOptions.find((o) => o.value === documentLayout.font) : null;

  if (loading) {
    return (
      <Box pos="relative" sx={{ width: '100%', minHeight: '70vh' }}>
        <LoadingOverlay visible overlayBlur={2} loaderProps={{ size: 'xl' }} />;
      </Box>
    );
  }

  if (!documentLayout) return null;

  return (
    <PanePaper title="Layout">
      <SimpleGrid cols={2} breakpoints={[{ maxWidth: 900, cols: 1 }]}>
        <Stack>
          {logoAvailable && (
            <PanePaper>
              <Title order={5}> Logo </Title>
              <Space h="xs" />
              <Text size="xs" color="dimmed">
                Größe
              </Text>
              <Space h="xs" />
              <SegmentedControl
                fullWidth
                data={DataS.logoSizeOptions}
                value={logoSize}
                onChange={(val) => patchDocumentLayout(PdfS.Layout.LOGO_SIZE, val)}
                mb={20}
                data-testid={TestS.DocumentLayout.CONTROL_LOGO_SIZE}
              />
              <Text size="xs" color="dimmed">
                Position
              </Text>
              <Space h="xs" />
              <Slider
                min={-260}
                max={540}
                defaultValue={logoPosition}
                onChangeEnd={(val: number) => patchDocumentLayout(PdfS.Layout.LOGO_POSITION, val)}
                marks={[
                  { value: -100, label: '20%' },
                  { value: 140, label: '50%' },
                  { value: 380, label: '80%' },
                ]}
                mb={20}
                data-testid={TestS.DocumentLayout.SLIDER_LOGO_POSITION}
              />
              <Space h="xl" />
              <Checkbox
                label={<Text> Logo nur für Online-Portal verwenden</Text>}
                checked={hideLogoOnDocuments}
                onChange={(e) => patchDocumentLayout(PdfS.Layout.HIDE_LOGO, e.currentTarget.checked)}
                mb={10}
                data-testid={TestS.DocumentLayout.TOGGLE_LOGO_ONLY_FOR_ONLINE_PORTAL}
              />
            </PanePaper>
          )}

          <PanePaper>
            <ThemeTitle order={5}>Profilinfos</ThemeTitle>
            <Select
              mt="sm"
              label="Wo sollen Deine Infos angezeigt werden?"
              data={DataS.boxPlacementOptions()}
              value={infoBoxPlacement}
              onChange={(val) => patchDocumentLayout(PdfS.Layout.BOX_PLACEMENT, val)}
              styles={{ rightSection: { pointerEvents: 'none' } }}
              rightSection={<IconChevronDown size={14} />}
              rightSectionWidth={25}
              itemComponent={AutocompleteResourceTypeItem}
            />
            <Space h="xl" />
            <ThemeText size="xs" color="dimmed">
              Namensangaben
            </ThemeText>
            <Space h="xs" />
            <SegmentedControl
              fullWidth
              data={infoBoxPlacement ? DataS.getContactNameOptions(infoBoxPlacement) : []}
              value={contactName}
              onChange={(val: string) => patchDocumentLayout(PdfS.Layout.CONTACT_NAME, val)}
              data-testid={TestS.DocumentLayout.CONTROL_CONTACT_NAME_PREFERENCE}
            />
            <Space h="xl" />
            <ThemeText size="xs" color="dimmed">
              Angaben zur/zum Inhaber:in
            </ThemeText>
            <Space h="xs" />
            <Checkbox
              checked={showFounderPersonalName}
              label="Inhaber:in namentlich anzeigen"
              mb={10}
              onChange={(e) => patchDocumentLayout(PdfS.Layout.SHOW_FOUNDER_NAME, e.currentTarget.checked)}
              data-testid={TestS.DocumentLayout.TOGGLE_SHOW_FOUNDER_PERSONAL_NAME}
            />
            <ThemeText size="xs" color="dimmed">
              Bankverbindung
            </ThemeText>
            <Space h="xs" />
            <Checkbox
              checked={hideBankAccount}
              label="Bankverbindung nur auf Rechnungen anzeigen"
              mb={10}
              onChange={(e) => patchDocumentLayout(PdfS.Layout.HIDE_BANK_ACCOUNT, e.currentTarget.checked)}
              data-testid={TestS.DocumentLayout.TOGGLE_HIDE_BANK_ACCOUNT}
            />
          </PanePaper>

          <PanePaper>
            <ThemeTitle order={5}>QR-Codes</ThemeTitle>
            <ThemeText mt="md">Füge QR-Codes für Rechnungen hinzu, um schnell und einfach bezahlt zu werden:</ThemeText>
            <Space h="xl" />
            <Checkbox
              checked={includeEpcQrCode}
              label="SEPA-QR-Code für schnelle Banküberweisung"
              mb={10}
              onChange={(e) => patchDocumentLayout('includeEpcQrCode', e.currentTarget.checked)}
            />
            <Checkbox
              checked={includePaypalQrCode}
              label="PayPal-QR-Code für schnelle PayPal-Zahlung"
              mb={10}
              onChange={(e) => patchDocumentLayout('includePaypalQrCode', e.currentTarget.checked)}
            />
            {includePaypalQrCode && bankAccount && (
              <>
                <BookitupTextInput
                  required
                  label="PayPal Me Alias"
                  defaultValue={paypalUserId ?? ''}
                  onBlurSave={(val) => editBankAccount({ paypalUserId: val })}
                />
                <Text size="xs" color="dimmed" mt={5}>
                  Du kannst{' '}
                  <Text
                    span
                    component="a"
                    color="blue"
                    href="https://www.paypal.com/paypalme"
                    target="_blank"
                    sx={{ cursor: 'pointer' }}
                  >
                    hier
                  </Text>{' '}
                  deinen PayPal Me Alias einsehen bzw. erstellen.
                </Text>
              </>
            )}
          </PanePaper>
          <PanePaper>
            <Title order={5}> Stile </Title>
            <Space h="xl" />
            <Select
              label="Oberer Abstand ab 2. Seite (nicht in Vorschau sichtbar)"
              icon={<IconArrowDownBar size={20} />}
              data={DataS.documentLayoutMarginTopOptions}
              value={documentLayout.marginTop + ''}
              onChange={(val: string) => patchDocumentLayout(PdfS.Layout.MARGIN_TOP, val)}
              styles={{ rightSection: { pointerEvents: 'none' } }}
              rightSection={<IconChevronDown size={16} />}
              rightSectionWidth={35}
            />
            <Space h="md" />
            <Select
              label="Unterer Abstand"
              icon={<IconArrowDownBar size={20} />}
              data={DataS.documentLayoutBottomMarginPageScopeOptions}
              value={documentLayout.bottomMarginPageScope + ''}
              onChange={(val: string) => patchDocumentLayout(PdfS.Layout.BOTTOM_MARGIN_PAGE_SCOPE, val)}
              styles={{ rightSection: { pointerEvents: 'none' } }}
              rightSection={<IconChevronDown size={16} />}
              rightSectionWidth={35}
            />
            <Space h="md" />
            <Text size="sm" color="dimmed">
              Justiere Deine Seitennummer
            </Text>
            <Space h="xs" />
            <SegmentedControl
              fullWidth
              mb="md"
              data={DataS.pageNumberPositionOptions}
              value={pageNumberPosition?.toUpperCase()}
              onChange={(val: string) => patchDocumentLayout(PdfS.Layout.PAGE_POSITION, val)}
              data-testid={TestS.DocumentLayout.CONTROL_PAGE_NUMBER_POSITION}
            />
            <Space h="xs" />
            <Select
              label="Schriftart"
              icon={<IconLetterCase size={20} />}
              data={DataS.fontOptions}
              value={selectedFontValue?.value}
              onChange={(val: string) => patchDocumentLayout(PdfS.Layout.FONT, val)}
              data-testid={TestS.DocumentLayout.SELECT_FONT}
              styles={{ rightSection: { pointerEvents: 'none' } }}
              rightSection={<IconChevronDown size={16} />}
              rightSectionWidth={35}
            />
            <Space h="md" />
            <Select
              mt="sm"
              label="Dokument-Überschrift wie 'Angebot' oder 'Vertrag'"
              data={DataS.headerFontSizeOptions}
              value={headerFontSize + ''}
              onChange={(val) => patchDocumentLayout(PdfS.Layout.HEADER_FONT_SIZE, val)}
              styles={{ rightSection: { pointerEvents: 'none' } }}
              rightSection={<IconChevronDown size={14} />}
              rightSectionWidth={25}
              itemComponent={AutocompleteResourceTypeItem}
            />
            <Select
              mt="sm"
              label="Überschriften von Produkten, Absätzen, Fragen"
              data={DataS.defaultFontSizeOptions}
              value={defaultFontSize + ''}
              onChange={(val) => patchDocumentLayout(PdfS.Layout.DEFAULT_FONT_SIZE, val)}
              styles={{ rightSection: { pointerEvents: 'none' } }}
              rightSection={<IconChevronDown size={14} />}
              rightSectionWidth={25}
              itemComponent={AutocompleteResourceTypeItem}
            />
            <Select
              mt="sm"
              label="Fließtext wie Produktbeschreibungen und Vertragstexte"
              data={DataS.subtextFontSizeOptions}
              value={subtextFontSize + ''}
              onChange={(val) => patchDocumentLayout(PdfS.Layout.SUBTEXT_FONT_SIZE, val)}
              styles={{ rightSection: { pointerEvents: 'none' } }}
              rightSection={<IconChevronDown size={14} />}
              rightSectionWidth={25}
              itemComponent={AutocompleteResourceTypeItem}
            />
            {infoBoxPlacement != 'NONE' && (
              <Select
                mt="sm"
                label="Profil-Infos"
                data={DataS.profileInfoFontSizeOptions}
                value={profileInfoFontSize + ''}
                onChange={(val) => patchDocumentLayout(PdfS.Layout.PROFILE_INFO_FONT_SIZE, val)}
                styles={{ rightSection: { pointerEvents: 'none' } }}
                rightSection={<IconChevronDown size={14} />}
                rightSectionWidth={25}
                itemComponent={AutocompleteResourceTypeItem}
              />
            )}
            <Space h="lg" />
            <Checkbox
              label={<Text>Transparenter Hintergrund</Text>}
              checked={backgroundTransparent}
              onChange={(e) => patchDocumentLayout(PdfS.Layout.BACKGROUND_TRASPARENT, e.currentTarget.checked)}
              mb={10}
              data-testid={TestS.DocumentLayout.TOGGLE_TRANSPARENT_BACKGROUND}
            />
            <Group>
              <ColorInput
                disallowInput
                value={fontColor}
                onChangeEnd={(val) => patchDocumentLayout(PdfS.Layout.COLOR, val)}
                label="Schrift-Farbe"
                format="hex"
                swatches={DataS.monochromeColors}
              />
              {!backgroundTransparent && (
                <ColorInput
                  disallowInput
                  value={backgroundColor}
                  onChangeEnd={(val) => patchDocumentLayout(PdfS.Layout.BACKGROUND_COLOR, val)}
                  label="Hintergrund-Farbe"
                  format="hex"
                  swatches={DataS.monochromeColors}
                />
              )}
            </Group>
            <Space h="xl" />
            <Checkbox
              label={<Text>Positionsnummer anzeigen</Text>}
              checked={showLineNumber}
              onChange={(e) => patchDocumentLayout(PdfS.Layout.SHOW_LINE_NUMBER, e.currentTarget.checked)}
              mb={10}
              data-testid={TestS.DocumentLayout.TOGGLE_POSITION_NUMBERS}
            />
            <Checkbox
              label={<Text>Rahmen anzeigen</Text>}
              checked={showBorders}
              onChange={(e) => patchDocumentLayout(PdfS.Layout.SHOW_BORDER, e.currentTarget.checked)}
              mb={10}
              data-testid={TestS.DocumentLayout.TOGGLE_SHOW_BORDERS}
            />
            <Checkbox
              label={<Text>Absender-Zeile ausblenden oder individualisieren</Text>}
              checked={documentLayout.customSenderInfo != null}
              onChange={(e) =>
                patchDocumentLayout(PdfS.Layout.CUSTOM_SENDER_INFO, documentLayout.customSenderInfo == null ? '' : null)
              }
              mb={10}
            />
          </PanePaper>
          <TextInput
            defaultValue={customSenderInfo ?? ''}
            onBlur={(e) => patchDocumentLayout(PdfS.Layout.CUSTOM_SENDER_INFO, e.currentTarget.value)}
            label={<Text size="xs">Individuelle Absender-Zeile (darf leer sein)</Text>}
          />
        </Stack>
        <PDFViewer
          url={`${process.env.REACT_APP_PDF_SERVICE_URL}/documentLayouts/sample`}
          documentLayout={documentLayout}
          lastModified={lastModified}
        />
      </SimpleGrid>
    </PanePaper>
  );
};

export default DocumentLayoutSettings;
